import { Constants } from "@eagerdog/constants";
import { IEventRegisterRow } from "../../../services/helper.service.js";
import { IShow } from "@eagerdog/interfaces";

export namespace AKCConformationJunior {
  export const getForm = (show?: IShow): IEventRegisterRow[] => {
    // Helper function to check if an element/level is available in the show
    const isElementAvailable = (elementOrLevel: string): boolean => {
      if (!show?.show_elements) return true; // If no show provided, show all options
      
      const result = show.show_elements.some(se => {
        // Check if the element/level matches directly
        // For Junior Showmanship, we now save each class with show_element="Junior Showmanship" and level="JS - Novice" (etc.)
        // So we just need to check if the level matches
        if (se.show_element === elementOrLevel || se.level === elementOrLevel) {
          return true;
        }
        
        return false;
      });
      
      return result;
    };

    // Filter conformation options based on available show elements
    const conformationOptions = [
      {
        value: Constants.dog_class_element_level.akc_4_6_month_puppy,
        id: Constants.dog_class_element_level.akc_4_6_month_puppy,
      },
      {
        value: Constants.dog_class_element_level.akc_6_9_month_puppy,
        id: Constants.dog_class_element_level.akc_6_9_month_puppy,
      },
      {
        value: Constants.dog_class_element_level.akc_9_12_month_puppy,
        id: Constants.dog_class_element_level.akc_9_12_month_puppy,
      },
      {
        value: Constants.dog_class_element_level.akc_12_15_month_puppy,
        id: Constants.dog_class_element_level.akc_12_15_month_puppy,
      },
      {
        value: Constants.dog_class_element_level.akc_15_18_month_puppy,
        id: Constants.dog_class_element_level.akc_15_18_month_puppy,
      },
      {
        value: Constants.dog_class_element_level.akc_12_18_month_puppy,
        id: Constants.dog_class_element_level.akc_12_18_month_puppy,
      },
      {
        value: Constants.dog_class_element_level.akc_18_24_month_puppy,
        id: Constants.dog_class_element_level.akc_18_24_month_puppy,
      },
      {
        value: Constants.dog_class_element_level.akc_under_12_month_bred_by_exhibitor,
        id: Constants.dog_class_element_level.akc_under_12_month_bred_by_exhibitor,
      },
      {
        value: Constants.dog_class_element_level.akc_adult_bred_by_exhibitor,
        id: Constants.dog_class_element_level.akc_adult_bred_by_exhibitor,
      },
      {
        value: Constants.dog_class_element_level.akc_amateur_owner_handler,
        id: Constants.dog_class_element_level.akc_amateur_owner_handler,
      },
      {
        value: Constants.dog_class_element_level.akc_american_bred,
        id: Constants.dog_class_element_level.akc_american_bred,
      },
      {
        value: Constants.dog_class_element_level.akc_open,
        id: Constants.dog_class_element_level.akc_open,
      },
      {
        value: Constants.dog_class_element_level.akc_novice,
        id: Constants.dog_class_element_level.akc_novice,
      },
      {
        value: Constants.dog_class_element_level.akc_miscellaneous,
        id: Constants.dog_class_element_level.akc_miscellaneous,
      },
      {
        value: Constants.dog_class_element_level.akc_best_of_breed,
        id: Constants.dog_class_element_level.akc_best_of_breed,
      },
      {
        value: Constants.dog_class_element_level.akc_veteran_6_8_years,
        id: Constants.dog_class_element_level.akc_veteran_6_8_years,
      },
      {
        value: Constants.dog_class_element_level.akc_veteran_8_10_years,
        id: Constants.dog_class_element_level.akc_veteran_8_10_years,
      },
      {
        value: Constants.dog_class_element_level.akc_veteran_8_11_years,
        id: Constants.dog_class_element_level.akc_veteran_8_11_years,
      },
      {
        value: Constants.dog_class_element_level.akc_veteran_10_12_years,
        id: Constants.dog_class_element_level.akc_veteran_10_12_years,
      },
      {
        value: Constants.dog_class_element_level.akc_veteran_11_plus_years,
        id: Constants.dog_class_element_level.akc_veteran_11_plus_years,
      },
      {
        value: Constants.dog_class_element_level.akc_veteran_7_plus_years,
        id: Constants.dog_class_element_level.akc_veteran_7_plus_years,
      },
      {
        value: Constants.dog_class_element_level.akc_veteran_8_plus_years,
        id: Constants.dog_class_element_level.akc_veteran_8_plus_years,
      },
      {
        value: Constants.dog_class_element_level.akc_veteran_10_plus_years,
        id: Constants.dog_class_element_level.akc_veteran_10_plus_years,
      },
      {
        value: Constants.dog_class_element_level.akc_veteran_13_plus_years,
        id: Constants.dog_class_element_level.akc_veteran_13_plus_years,
      },
      {
        value: Constants.dog_class_element_level.akc_veteran_7_10_years,
        id: Constants.dog_class_element_level.akc_veteran_7_10_years,
      },
      {
        value: Constants.dog_class_element_level.akc_veteran_12_plus_years,
        id: Constants.dog_class_element_level.akc_veteran_12_plus_years,
      },
      {
        value: Constants.dog_class_element_level.akc_18_24_month_puppy,
        id: Constants.dog_class_element_level.akc_18_24_month_puppy,
      },
    ].filter(option => isElementAvailable(option.value));

    // Filter non-licensed options based on available show elements
    const nonLicensedOptions = [
      {
        value: Constants.dog_class_element_level.akc_futurity,
        id: Constants.dog_class_element_level.akc_futurity,
      },
      {
        value: Constants.dog_class_element_level.akc_sled_dog,
        id: Constants.dog_class_element_level.akc_sled_dog,
      },
      {
        value: Constants.dog_class_element_level.akc_hunting_class,
        id: Constants.dog_class_element_level.akc_hunting_class,
      },
      {
        value: Constants.dog_class_element_level.akc_field_trial_class,
        id: Constants.dog_class_element_level.akc_field_trial_class,
      },
      {
        value: Constants.dog_class_element_level.akc_teams,
        id: Constants.dog_class_element_level.akc_teams,
      },
      {
        value: Constants.dog_class_element_level.akc_parade_of_titleholders,
        id: Constants.dog_class_element_level.akc_parade_of_titleholders,
      },
      {
        value: Constants.dog_class_element_level.akc_coursing,
        id: Constants.dog_class_element_level.akc_coursing,
      },
      {
        value: Constants.dog_class_element_level.akc_best_head,
        id: Constants.dog_class_element_level.akc_best_head,
      },
      {
        value: Constants.dog_class_element_level.akc_best_movement,
        id: Constants.dog_class_element_level.akc_best_movement,
      },
      {
        value: Constants.dog_class_element_level.akc_best_field,
        id: Constants.dog_class_element_level.akc_best_field,
      },
    ].filter(option => isElementAvailable(option.value));

    // Filter junior showmanship options based on available show elements
    const allJuniorOptions = [
      // Old junior showmanship levels
      { id: Constants.dog_class_element_level.akc_novice_junior , value: Constants.dog_class_element_level.akc_novice_junior },
      { id: Constants.dog_class_element_level.akc_novice_intermediate , value: Constants.dog_class_element_level.akc_novice_intermediate },
      { id: Constants.dog_class_element_level.akc_novice_senior , value: Constants.dog_class_element_level.akc_novice_senior },
      { id: Constants.dog_class_element_level.akc_open_junior , value: Constants.dog_class_element_level.akc_open_junior },
      { id: Constants.dog_class_element_level.akc_open_intermediate , value: Constants.dog_class_element_level.akc_open_intermediate },
      { id: Constants.dog_class_element_level.akc_open_senior , value: Constants.dog_class_element_level.akc_open_senior },
      { id: Constants.dog_class_element_level.akc_master_class , value: Constants.dog_class_element_level.akc_master_class },
      // New "JS - " prefixed junior showmanship levels
      { id: Constants.dog_class_element_level.akc_junior_showmanship_novice, value: Constants.dog_class_element_level.akc_junior_showmanship_novice },
      { id: Constants.dog_class_element_level.akc_junior_showmanship_open, value: Constants.dog_class_element_level.akc_junior_showmanship_open },
      { id: Constants.dog_class_element_level.akc_junior_showmanship_master, value: Constants.dog_class_element_level.akc_junior_showmanship_master },
    ];
    const juniorShowmanshipOptions = allJuniorOptions.filter(option => isElementAvailable(option.value));

    const formRows: IEventRegisterRow[] = [];

    // Only add conformation section if there are available options
    if (conformationOptions.length > 0) {
      formRows.push({
        allowDuplicates: false,
        inputs: [
          {
            type: "checkbox",
            label: Constants.dog_class_element.conformation,
            multiSelect: false,
            options: conformationOptions,
          },
        ],
      });
    }

    // Only add non-licensed section if there are available options
    if (nonLicensedOptions.length > 0) {
      formRows.push({
        allowDuplicates: false,
        inputs: [
          {
            type: "checkbox",
            label: Constants.dog_class_element.conformation_non_licensed,
            multiSelect: true,
            options: nonLicensedOptions,
          },
        ],
      });
    }

    // Only add junior showmanship section if there are available options
    if (juniorShowmanshipOptions.length > 0) {
      formRows.push({
        allowDuplicates: false,
        inputs: [
          {
            type: "checkbox",
            label: Constants.dog_class_element.junior_showmanship,
            multiSelect: true,
            options: juniorShowmanshipOptions
          },
        ],
      });
    }

    return formRows;
  };
}